import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import InteractiveTimeline from './components/InteractiveTimeline';
import InvestmentsSection from './components/InvestmentsSection';
import EducationSection from './components/EducationSection';
import ContentShowcase from './components/ContentShowcase';
import Pickleball from './components/Pickleball';
import Footer from './components/Footer';
import AdminPanel from './components/AdminPanel';
import './App.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hide Emergent badge for paid users
if (typeof window !== 'undefined') {
  const hideBadge = () => {
    const badge = document.getElementById('emergent-badge');
    if (badge) {
      badge.style.display = 'none';
      badge.style.visibility = 'hidden';
    }
  };
  // Run immediately and also after DOM loads
  hideBadge();
  document.addEventListener('DOMContentLoaded', hideBadge);
  // Also run after a delay in case it's injected later
  setTimeout(hideBadge, 1000);
  setTimeout(hideBadge, 3000);
}

// Cookie Consent Banner Component
function CookieConsent({ onAccept, onDecline }) {
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-slate-900 text-white p-4 z-50 shadow-lg">
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex-1">
          <p className="text-sm">
            We use privacy-preserving analytics to improve your experience. No personal data is stored. 
            <a href="/privacy" className="underline ml-1 text-amber-400 hover:text-amber-300">Learn more</a>
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onDecline}
            className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
          >
            Decline
          </button>
          <button
            onClick={onAccept}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}

// Analytics tracker hook with consent
function usePageTracking(hasConsent) {
  const location = useLocation();
  
  useEffect(() => {
    if (!hasConsent) return;
    
    // Track page view on route change
    const trackPageView = async () => {
      try {
        await fetch(`${API_URL}/api/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            page: location.pathname,
            consent: hasConsent
          })
        });
      } catch (e) {
        // Silent fail
      }
    };
    trackPageView();
  }, [location, hasConsent]);
}

// Main Portfolio Page
function PortfolioPage() {
  return (
    <>
      <Navigation />
      <Hero />
      <InteractiveTimeline />
      <div id="investments">
        <InvestmentsSection />
      </div>
      <div id="education">
        <EducationSection />
      </div>
      <div id="projects">
        <ContentShowcase />
      </div>
      <Pickleball />
      <Footer />
    </>
  );
}

// Privacy Policy Page
function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <div className="max-w-3xl mx-auto px-6 py-20">
        <h1 className="text-4xl font-bold text-slate-800 mb-8">Privacy Policy</h1>
        
        <div className="prose prose-slate">
          <p className="text-slate-600 mb-6">Last updated: January 2025</p>
          
          <h2 className="text-2xl font-semibold text-slate-800 mt-8 mb-4">Analytics</h2>
          <p className="text-slate-600 mb-4">
            We use privacy-preserving analytics to understand how visitors use our site. Our analytics:
          </p>
          <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
            <li>Do not use cookies or local storage for tracking</li>
            <li>Do not collect any personally identifiable information (PII)</li>
            <li>Double-hash IP addresses (cannot be reversed)</li>
            <li>Only store aggregated data (device type, browser category)</li>
            <li>Auto-delete detailed records after 90 days</li>
            <li>Do not share data with third parties</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-slate-800 mt-8 mb-4">Contact Form</h2>
          <p className="text-slate-600 mb-4">
            When you submit the contact form, we collect:
          </p>
          <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
            <li>Name (as provided)</li>
            <li>Social media handle (optional)</li>
            <li>Message content</li>
            <li>Email (optional, for response)</li>
          </ul>
          <p className="text-slate-600 mb-6">
            This information is only used to respond to your inquiry and is not shared with third parties.
          </p>
          
          <h2 className="text-2xl font-semibold text-slate-800 mt-8 mb-4">Your Rights</h2>
          <p className="text-slate-600 mb-4">
            Under GDPR, CCPA, and other privacy laws, you have the right to:
          </p>
          <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
            <li>Opt out of analytics tracking (via consent banner)</li>
            <li>Request deletion of any submitted contact form data</li>
            <li>Know what data we have about you (contact us to request)</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-slate-800 mt-8 mb-4">Contact</h2>
          <p className="text-slate-600">
            For privacy-related inquiries, contact us through the contact form on this website.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}

// Wrapper component that uses the tracking hook
function AppContent() {
  const [showConsent, setShowConsent] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  
  useEffect(() => {
    // Check if consent was already given/declined
    const consentStatus = localStorage.getItem('analytics_consent');
    if (consentStatus === null) {
      setShowConsent(true);
    } else {
      setHasConsent(consentStatus === 'accepted');
    }
  }, []);
  
  const handleAccept = () => {
    localStorage.setItem('analytics_consent', 'accepted');
    setHasConsent(true);
    setShowConsent(false);
  };
  
  const handleDecline = () => {
    localStorage.setItem('analytics_consent', 'declined');
    setHasConsent(false);
    setShowConsent(false);
  };
  
  usePageTracking(hasConsent);
  
  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<PortfolioPage />} />
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/privacy" element={<PrivacyPage />} />
      </Routes>
      {showConsent && <CookieConsent onAccept={handleAccept} onDecline={handleDecline} />}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
